import { useMemo } from 'react';
import { Typography } from '@weareopenr/component-library';
import styles from './RoleModal.module.scss';
import { IApplicationSection } from './permissions';
import { deriveSummary } from './summary';
import SummaryLines from './SummaryLines';

interface IRoleSummaryProps {
  sections: IApplicationSection[];
  selectedPermissions: string[];
}

const RoleSummary = ({ sections, selectedPermissions }: IRoleSummaryProps) => {
  const summary = useMemo(
    () => deriveSummary(sections, selectedPermissions),
    [sections, selectedPermissions],
  );
  const countLabel = summary.selectedCount === 1 ? 'permission' : 'permissions';

  return (
    <aside aria-label="Role summary" className={styles['summary-card']}>
      <Typography size="small" weight="semi-bold">
        This role can
      </Typography>
      {summary.lines.length === 0 && summary.unrecognised.length === 0 ? (
        <Typography size="small" className={styles['summary-muted']}>
          Select permissions to build this role.
        </Typography>
      ) : (
        <SummaryLines lines={summary.lines}>
          {summary.unrecognised.map((slug) => (
            <li key={slug}>
              <Typography size="small">{slug}</Typography>
            </li>
          ))}
        </SummaryLines>
      )}
      {summary.cannotNote && (
        <div className={styles['summary-note']}>
          <Typography size="xsmall" className={styles['summary-muted']}>
            {summary.cannotNote}
          </Typography>
        </div>
      )}
      {summary.venueCreateNote && (
        <div role="note" className={styles['summary-advisory']}>
          <Typography size="xsmall">{summary.venueCreateNote}</Typography>
        </div>
      )}
      {summary.includedBlurb && (
        <>
          <div className={styles['summary-divider']} />
          <Typography size="xsmall" weight="semi-bold" className={styles['summary-muted']}>
            Included for every role
          </Typography>
          <Typography size="xsmall" className={styles['summary-muted']}>
            {summary.includedBlurb}
          </Typography>
        </>
      )}
      <div className={styles['summary-divider']} />
      <Typography size="small" weight="semi-bold">
        {`${summary.selectedCount} ${countLabel} selected`}
      </Typography>
    </aside>
  );
};

export default RoleSummary;
